import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Download, LogOut, Trash2, Mail, Phone, MapPin,
  ArrowUpDown, X, StickyNote, ExternalLink,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";

const STATUSES = [
  { id: "new", label: "Nouveau" },
  { id: "contacted", label: "Contacté" },
  { id: "won", label: "Gagné" },
  { id: "lost", label: "Perdu" },
];

const STATUS_COLORS = {
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  new: "bg-ink/5 text-ink border-ink/15",
};

const statusColor = (s) => STATUS_COLORS[s] || STATUS_COLORS.new;

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, won: 0 });
  const [selected, setSelected] = useState(null);
  const [fetching, setFetching] = useState(true);

  const fetchLeads = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/leads", { params: { search, sort, order } });
      setLeads(data);
      const s = await api.get("/leads/stats/summary");
      setStats(s.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to load leads:", err);
      }
      toast.error("Impossible de charger les demandes.");
    } finally {
      setFetching(false);
    }
  }, [search, sort, order]);

  useEffect(() => {
    if (user) fetchLeads();
  }, [user, sort, order, fetchLeads]);

  useEffect(() => {
    if (!user) return undefined;
    const t = setTimeout(fetchLeads, 250);
    return () => clearTimeout(t);
  }, [search, user, fetchLeads]);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Demande supprimée.");
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const onUpdateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Erreur.");
    }
  };

  const onUpdateNotes = async (id, notes) => {
    try {
      await api.patch(`/leads/${id}/notes`, { notes });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes } : l)));
      toast.success("Note enregistrée.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    }
  };

  const toggleSort = (col) => {
    if (sort === col) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else { setSort(col); setOrder("desc"); }
  };

  const headerCells = useMemo(() => ([
    { id: "created_at", label: "Date" },
    { id: "last_name", label: "Contact" },
    { id: "company", label: "Entreprise" },
    { id: "city", label: "Ville" },
    { id: "budget", label: "Budget" },
    { id: "status", label: "Statut" },
  ]), []);

  if (loading) return null;
  if (!user) return <Navigate to="/admin" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper-soft text-ink">
      {/* Topbar */}
      <header className="bg-white border-b border-ink/10 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-ink/60">{user.email}</span>
            <a
              href={`${API}/leads/export.csv`}
              data-testid="admin-export-csv"
              className="hidden md:inline-flex items-center gap-2 h-10 px-4 border border-ink/15 text-sm hover:bg-ink hover:text-white transition-colors"
            >
              <Download size={14} /> Export CSV
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 h-10 px-4 bg-ink text-white text-sm hover:bg-ink-600 transition-colors"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <p className="font-mono-label text-ink/55"><span className="rule" />Espace admin</p>
          <h1 className="mt-4 font-display text-4xl md:text-5xl tracking-tight">Demandes reçues</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10 mb-8">
          {[
            { label: "Total", v: stats.total },
            { label: "Nouveau", v: stats.new },
            { label: "Contacté", v: stats.contacted },
            { label: "Gagné", v: stats.won },
          ].map((s) => (
            <div key={s.label} className="bg-white p-6">
              <p className="font-mono-label text-ink/55">{s.label}</p>
              <p className="mt-2 font-display text-4xl text-ink">{s.v}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              data-testid="admin-search"
              placeholder="Rechercher (nom, email, entreprise, ville, téléphone…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-12 pl-11 pr-4 bg-white border border-ink/15 focus:outline-none focus:border-ink"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-ink/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-paper-soft border-b border-ink/10">
              <tr>
                {headerCells.map((h) => (
                  <th
                    key={h.id}
                    onClick={() => toggleSort(h.id)}
                    className="text-left px-5 py-4 font-mono-label text-ink/60 cursor-pointer select-none whitespace-nowrap"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {h.label}
                      <ArrowUpDown size={11} className={sort === h.id ? "text-ink" : "text-ink/30"} />
                    </span>
                  </th>
                ))}
                <th className="px-5 py-4 font-mono-label text-ink/60 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fetching && (
                <tr><td colSpan={7} className="p-10 text-center text-ink/55">Chargement…</td></tr>
              )}
              {!fetching && leads.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-ink/55">Aucune demande pour le moment.</td></tr>
              )}
              {leads.map((l) => (
                <tr
                  key={l.id}
                  data-testid={`lead-row-${l.id}`}
                  onClick={() => setSelected(l)}
                  className="border-b border-ink/5 last:border-0 cursor-pointer hover:bg-paper-soft"
                >
                  <td className="px-5 py-4 text-ink/70 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-ink">{l.first_name} {l.last_name}</p>
                    <p className="text-ink/55 text-xs">{l.email}</p>
                  </td>
                  <td className="px-5 py-4 text-ink/80">{l.company || "—"}</td>
                  <td className="px-5 py-4 text-ink/80">{l.city}</td>
                  <td className="px-5 py-4 text-ink/80">{l.budget || "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex px-2.5 py-1 text-xs border ${statusColor(l.status)}`}>
                      {STATUSES.find((s) => s.id === l.status)?.label || l.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(l.id); }}
                      className="inline-flex items-center justify-center h-8 w-8 text-ink/50 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`lead-delete-${l.id}`}
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Drawer */}
      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onSaveNotes={onUpdateNotes} onStatusChange={onUpdateStatus} />}
    </div>
  );
}

const LeadDrawer = ({ lead, onClose, onSaveNotes, onStatusChange }) => {
  const [notes, setNotes] = useState(lead.notes || "");
  // Reset notes textarea when the selected lead changes
  useEffect(() => {
    setNotes(lead.notes || "");
  }, [lead.id, lead.notes]);

  return (
    <div className="fixed inset-0 z-50" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-ink/10 px-8 py-5 flex items-center justify-between">
          <div>
            <p className="font-mono-label text-ink/55">Demande #{lead.id.slice(0, 8)}</p>
            <p className="font-display text-2xl text-ink mt-1">{lead.first_name} {lead.last_name}</p>
          </div>
          <button onClick={onClose} className="h-10 w-10 inline-flex items-center justify-center border border-ink/15 hover:bg-ink hover:text-white" data-testid="drawer-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-8 space-y-8">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Email"><a className="link-under inline-flex items-center gap-1.5 break-all" href={`mailto:${lead.email}`}><Mail size={12} />{lead.email}</a></Field>
            <Field label="Téléphone"><a className="link-under inline-flex items-center gap-1.5" href={`tel:${lead.phone}`}><Phone size={12} />{lead.phone}</a></Field>
            <Field label="Entreprise">{lead.company || "—"}</Field>
            <Field label="Ville"><span className="inline-flex items-center gap-1.5"><MapPin size={12} />{lead.city}</span></Field>
            <Field label="Secteur">{lead.sector || "—"}</Field>
            <Field label="Site existant ?">{lead.has_website || "—"}</Field>
            <Field label="Objectif">{lead.main_goal || "—"}</Field>
            <Field label="Budget">{lead.budget || "—"}</Field>
          </div>

          <div>
            <p className="font-mono-label text-ink/55 mb-2">Services demandés</p>
            <div className="flex flex-wrap gap-2">
              {lead.services?.length ? lead.services.map((s) => (
                <span key={s} className="px-3 py-1 bg-paper-soft border border-ink/15 text-xs">{s}</span>
              )) : <span className="text-ink/55 text-sm">—</span>}
            </div>
          </div>

          <div>
            <p className="font-mono-label text-ink/55 mb-2">Description</p>
            <p className="text-ink/80 whitespace-pre-wrap leading-relaxed">{lead.description || "—"}</p>
          </div>

          <div>
            <p className="font-mono-label text-ink/55 mb-2">Statut</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => onStatusChange(lead.id, s.id)}
                  data-testid={`status-${s.id}`}
                  className={`px-3 h-9 text-xs border ${lead.status === s.id ? "bg-ink text-white border-ink" : "bg-white text-ink/80 border-ink/15 hover:border-ink"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-label text-ink/55 mb-2 inline-flex items-center gap-2"><StickyNote size={12} /> Notes internes</p>
            <textarea
              data-testid="lead-notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-paper-soft border border-ink/15 p-3 text-sm focus:outline-none focus:border-ink resize-none"
              placeholder="Ajoutez ici votre suivi commercial..."
            />
            <button
              onClick={() => onSaveNotes(lead.id, notes)}
              data-testid="lead-notes-save"
              className="mt-3 inline-flex items-center gap-2 h-10 px-4 bg-ink text-white text-sm hover:bg-ink-600"
            >
              Enregistrer la note
            </button>
          </div>

          <p className="text-xs text-ink/50 inline-flex items-center gap-2 pt-4 border-t border-ink/10">
            <ExternalLink size={12} /> Reçu le {new Date(lead.created_at).toLocaleString("fr-FR")}
          </p>
        </div>
      </aside>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="font-mono-label text-ink/50 mb-1">{label}</p>
    <p className="text-ink">{children}</p>
  </div>
);
